// src/components/common/input/Input.tsx
"use client";

import React, { forwardRef, InputHTMLAttributes, ReactNode, useState } from "react";
import { useField } from "formik";
import { Eye, EyeOff } from "lucide-react";
import FieldError from "../field-error/FieldError";
import "./Input.css";

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "prefix" | "suffix"> {
  label?: string;
  name: string;                 // keep required for id/label association
  className?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  error?: string;               // external error (standalone mode)
  canTogglePassword?: boolean;
  /** If true, DO NOT use Formik; rely on value/onChange from props */
  standalone?: boolean;
}

/** Forward ref so parent (e.g., Modal) can focus the input */
const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    name,
    className = "",
    prefix,
    suffix,
    error,
    type = "text",
    canTogglePassword = true,
    standalone = false,
    ...rest
  },
  ref
) {
  // password toggle ---------------------------------------------------------
  const [showPassword, setShowPassword] = useState(false);
  const isPasswordField = type === "password";
  const inputType = isPasswordField ? (showPassword ? "text" : "password") : type;
  const handleTogglePassword = () => setShowPassword((prev) => !prev);

  // formik vs standalone ----------------------------------------------------
  let fieldProps: any = {};
  let metaError: string | undefined;

  if (!standalone) {
    const [field, meta] = useField(name);     // { name, value, onChange, onBlur }
    fieldProps = field;
    metaError = meta.touched ? meta.error : undefined;
  }
  // in standalone mode we rely entirely on `rest.value/onChange`

  const finalError = metaError || error;

  const wrapperClass =
    prefix || suffix || isPasswordField ? "input-wrapper with-affix" : "input-wrapper";

  return (
    <div className={wrapperClass}>
      {label && (
        <label htmlFor={name} className="input-label">
          {label}
        </label>
      )}

      <div className="input-inner">
        {prefix && <span className="input-prefix">{prefix}</span>}

        <input
          id={name}
          ref={ref}
          type={inputType}
          {...(standalone ? {} : fieldProps)}
          {...rest}
          className={`input-field ${className} ${
            prefix ? "with-prefix" : ""
          } ${suffix || isPasswordField ? "with-suffix" : ""} ${finalError ? "has-error" : ""}`}
        />

        {isPasswordField && canTogglePassword ? (
          <button
            type="button"
            className="input-suffix password-toggle"
            onClick={handleTogglePassword}
            aria-label="Toggle password visibility"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        ) : (
          suffix && <span className="input-suffix">{suffix}</span>
        )}
      </div>

      {finalError && <FieldError>{finalError}</FieldError>}
    </div>
  );
});

export default Input;
